import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import { Box, Button, Card, Typography } from '@mui/material'
import { alpha, styled } from '@mui/material/styles'
import type { OnwardSuggestionSource } from 'api/onwardAPI'
import type { OnwardQuoteSuggestion } from 'hooks/useOnwardBookings'

const SuggestionCard = styled(Card)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: 12,
  marginBottom: 10,
  border: '1px solid #e2e6ea',
  borderRadius: 8,
  backgroundColor: '#f7f7f7',
  boxShadow: 'none',
}))

const CardBody = styled(Box)({
  display: 'flex',
  alignItems: 'stretch',
  gap: 12,
})

const SOURCE_COLOUR: Record<
  OnwardSuggestionSource,
  'info' | 'warning' | 'grey'
> = {
  history: 'info',
  team: 'warning',
  popular: 'grey',
}

const sourceLabel = (
  source: OnwardSuggestionSource,
  airportIata?: string,
): string => {
  if (source === 'history') return "You've been here"
  if (source === 'team') return 'Company Suggestion'
  return airportIata ? `Popular from ${airportIata}` : 'Popular'
}

const ReasonChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'source',
})<{ source: OnwardSuggestionSource }>(({ theme, source }) => {
  const key = SOURCE_COLOUR[source]
  const tone =
    key === 'grey'
      ? { fg: theme.palette.grey[700], bg: theme.palette.grey[200] }
      : {
          fg: theme.palette[key].dark || theme.palette[key].main,
          bg: alpha(theme.palette[key].main, 0.12),
        }
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    padding: '2px 8px',
    borderRadius: 999,
    border: `1px solid ${alpha(tone.fg, 0.4)}`,
    backgroundColor: tone.bg,
    color: tone.fg,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    '& > .dot': {
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: tone.fg,
    },
  }
})

const ImagePanel = styled(Box)(() => ({
  width: 96,
  height: 72,
  alignSelf: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: '#00253d',
}))

const DetailsColumn = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minWidth: 0,
})

const TitleText = styled(Typography)({
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.25,
  color: '#00253d',
  textWrap: 'pretty',
})

const DestinationText = styled(Typography)({
  fontSize: 10,
  fontWeight: 400,
  lineHeight: 1.3,
  color: '#5a6670',
  marginTop: 2,
})

const MetaRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 10,
  marginTop: 6,
  flexWrap: 'wrap',
})

const MetaItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  fontSize: 12,
  color: '#5a6670',
})

const PriceColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  flexShrink: 0,
})

const PriceText = styled(Typography)({
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1.1,
  color: '#00253d',
})

const PriceSubtext = styled(Typography)({
  fontSize: 10,
  color: '#5a6670',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
})

const BookButton = styled(Button)({
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 600,
  padding: '4px 12px',
  borderRadius: 6,
  whiteSpace: 'nowrap',
  minWidth: 0,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
})

export const formatOnwardPrice = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

const getDestinationDisplay = (suggestion: OnwardQuoteSuggestion): string => {
  const dest = suggestion.destination as any
  const formatted: string | undefined = dest?.formattedAddress
  if (typeof formatted === 'string' && formatted.trim()) {
    const firstPart = formatted.split(',')[0]?.trim()
    if (firstPart) return firstPart
  }
  return dest?.addressLine1 || dest?.town || dest?.postCode || 'Destination'
}

const prettifyVehicleName = (suggestion: OnwardQuoteSuggestion): string => {
  const details = suggestion.vehicleDetails
  if (details?.displayName) {
    return details.category && details.category !== 'Taxi'
      ? `${details.category}: ${details.displayName}`
      : details.displayName
  }
  return (suggestion.vehicleTypeName || 'Vehicle').replace(/_/g, ' ')
}

interface OnwardSuggestionCardProps {
  suggestion: OnwardQuoteSuggestion
  onSelect: (suggestion: OnwardQuoteSuggestion) => void
}

export const OnwardSuggestionCard: React.FC<OnwardSuggestionCardProps> = ({
  suggestion,
  onSelect,
}) => {
  const details = suggestion.vehicleDetails
  const vehicleTitle = prettifyVehicleName(suggestion)
  const seats = details?.seats
  const luggage = details?.luggage
  const iconSrc = details?.icon

  const source = suggestion.source
  const airportIata = suggestion.arrivalAirport?.iata

  return (
    <SuggestionCard elevation={0}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        {source ? (
          <ReasonChip source={source}>
            <span className="dot" />
            {sourceLabel(source, airportIata)}
          </ReasonChip>
        ) : (
          <Box />
        )}
        <Box sx={{ textAlign: 'right' }}>
          <PriceSubtext>from</PriceSubtext>
          <PriceText>
            {formatOnwardPrice(suggestion.cheapestPrice, suggestion.currency)}
          </PriceText>
        </Box>
      </Box>
      <CardBody>
        <ImagePanel>
          {iconSrc ? (
            <Box
              component="img"
              src={iconSrc}
              alt={vehicleTitle}
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <DirectionsCarIcon sx={{ fontSize: 36 }} />
          )}
        </ImagePanel>

        <DetailsColumn>
          <Box>
            <TitleText>{getDestinationDisplay(suggestion)}</TitleText>
            <DestinationText>{vehicleTitle}</DestinationText>
            <DestinationText>{details.example}</DestinationText>

            <MetaRow>
              {seats ? (
                <MetaItem>
                  <PeopleOutlineIcon sx={{ fontSize: 16, color: '#5a6670' }} />
                  <span>{seats}</span>
                </MetaItem>
              ) : null}
              {luggage ? (
                <MetaItem>
                  <WorkOutlineIcon sx={{ fontSize: 16, color: '#5a6670' }} />
                  <span>{luggage}</span>
                </MetaItem>
              ) : null}
            </MetaRow>
          </Box>
        </DetailsColumn>

        <PriceColumn>
          <BookButton
            variant="contained"
            size="small"
            onClick={() => onSelect(suggestion)}
          >
            Select
          </BookButton>
        </PriceColumn>
      </CardBody>
    </SuggestionCard>
  )
}

interface OnwardBookingCardsProps {
  suggestions: OnwardQuoteSuggestion[]
  onSelect: (suggestion: OnwardQuoteSuggestion) => void
}

const MAX_SUGGESTIONS = 3

const OnwardBookingCards: React.FC<OnwardBookingCardsProps> = ({
  suggestions,
  onSelect,
}) => {
  if (!suggestions.length) return null
  const visibleSuggestions = suggestions.slice(0, MAX_SUGGESTIONS)

  return (
    <Box>
      {visibleSuggestions.map((suggestion, index) => (
        <OnwardSuggestionCard
          key={`${suggestion.destination?.addressLine1 || ''}-${index}`}
          suggestion={suggestion}
          onSelect={onSelect}
        />
      ))}
    </Box>
  )
}

export default OnwardBookingCards
